import { useEffect, useMemo, useState } from "react";
import { ClipboardList, Loader2, Minus, Plus, Save } from "lucide-react";
import {
  getLeaveSettings,
  updateLeaveSettings,
  type LeaveSettingsPayload,
} from "../../api/leaveSettings";
import { useToast } from "../../hooks/useToast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import LeavePolicySettings from "./LeavePolicySettings";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const DEFAULT_SETTINGS: LeaveSettingsPayload = {
  sick_days: 12,
  annual_days: 15,
  casual_days: 10,
  maternity_enabled: false,
  maternity_days: 0,
  paternity_enabled: false,
  paternity_days: 0,
  compensatory_enabled: false,
  compensatory_days: 0,
  year_start_month: 1,
};

function leaveYearLabel(startMonth: number) {
  const startIdx = Math.max(1, Math.min(12, startMonth)) - 1;
  if (startMonth === 1) return "January 1 — December 31";
  const endIdx = (startIdx + 11) % 12;
  return `${MONTH_SHORT[startIdx]} 1 — ${MONTH_SHORT[endIdx]} ${[31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][endIdx]}`;
}

interface StepperProps {
  value: number;
  onChange: (n: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  unit?: string;
}

function Stepper({ value, onChange, disabled, min = 0, max = 365, unit }: StepperProps) {
  const clamp = (n: number) => Math.max(min, Math.min(max, Math.floor(n)));
  // Local string state — lets us own what's shown in the input so we don't
  // get the `type="number"` "012" / cursor-at-start problem when the
  // controlled value is 0. Resyncs whenever the parent value changes
  // (e.g. via the +/- buttons or after a save).
  const [display, setDisplay] = useState<string>(String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setDisplay(String(value));
  }, [value, focused]);

  return (
    <div className={`leave-stepper ${disabled ? "is-disabled" : ""}`}>
      <button
        type="button"
        className="leave-stepper-btn"
        onClick={() => !disabled && onChange(clamp(value - 1))}
        disabled={disabled || value <= min}
        aria-label="Decrease"
        tabIndex={disabled ? -1 : 0}
      >
        <Minus className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        className="leave-stepper-input"
        value={display}
        disabled={disabled}
        // On focus: blank the input if the current value is 0 so the
        // user's first digit becomes the value (no "012" / "50").
        // For any other value we select-all so a single digit replaces it.
        onFocus={(e) => {
          setFocused(true);
          if (value === 0) {
            setDisplay("");
          } else {
            // Defer select so the browser doesn't unselect immediately.
            const el = e.currentTarget;
            requestAnimationFrame(() => el.select());
          }
        }}
        onChange={(e) => {
          // Allow only digits while typing — strip everything else.
          const raw = e.target.value.replace(/[^0-9]/g, "");
          setDisplay(raw);
          if (raw === "") {
            // Empty mid-typing — don't push 0 yet; commit on blur.
            return;
          }
          const n = Number(raw);
          if (Number.isNaN(n)) return;
          onChange(clamp(n));
        }}
        onBlur={() => {
          setFocused(false);
          // If the user left the field empty, snap back to min (usually 0).
          if (display === "") {
            onChange(min);
            setDisplay(String(min));
            return;
          }
          // Reflect the canonical (clamped) value as plain text — drops
          // any leading zeros the user may have typed.
          const n = clamp(Number(display) || min);
          onChange(n);
          setDisplay(String(n));
        }}
      />
      <button
        type="button"
        className="leave-stepper-btn"
        onClick={() => !disabled && onChange(clamp(value + 1))}
        disabled={disabled || value >= max}
        aria-label="Increase"
        tabIndex={disabled ? -1 : 0}
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
      {unit && <span className="leave-stepper-unit">{unit}</span>}
    </div>
  );
}

interface PillToggleProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}

function PillToggle({ checked, onChange, label }: PillToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`leave-toggle ${checked ? "is-on" : ""}`}
    >
      <span className="leave-toggle-thumb" />
    </button>
  );
}

export default function LeaveSettings() {
  const [form, setForm] = useState<LeaveSettingsPayload>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getLeaveSettings();
        if (!mounted) return;
        setForm({
          sick_days: data.sick_days,
          annual_days: data.annual_days,
          casual_days: data.casual_days,
          maternity_enabled: data.maternity_enabled,
          maternity_days: data.maternity_days,
          paternity_enabled: data.paternity_enabled,
          paternity_days: data.paternity_days,
          compensatory_enabled: data.compensatory_enabled ?? false,
          compensatory_days: data.compensatory_days ?? 0,
          year_start_month: data.year_start_month || 1,
        });
      } catch (e: any) {
        const detail = e?.response?.data?.detail;
        toast({
          title: "Couldn't load leave settings",
          description: typeof detail === "string" ? detail : "Please try again.",
          variant: "destructive",
          duration: 3000,
        });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = <K extends keyof LeaveSettingsPayload>(
    key: K,
    value: LeaveSettingsPayload[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateLeaveSettings(form);
      const n = result.employees_affected;
      toast({
        title: "Leave settings saved",
        description:
          n > 0
            ? `Balances refreshed for ${n} employee${n === 1 ? "" : "s"}.`
            : "Settings saved.",
        duration: 3000,
      });
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      toast({
        title: "Couldn't save",
        description: typeof detail === "string" ? detail : "Please try again.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const totalAnnualDays = useMemo(
    () => (form.sick_days || 0) + (form.annual_days || 0) + (form.casual_days || 0),
    [form.sick_days, form.annual_days, form.casual_days]
  );

  if (loading) {
    return (
      <div className="leave-settings-loader">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>Loading allocations…</span>
      </div>
    );
  }

  return (
    <div className="leave-settings gt-fade-in">
      {/* Editorial header */}
      <header className="leave-hero" style={{ position: "relative" }}>
        <div className="gt-eyebrow-row">
          <span className="gt-leaf" />
          <span className="gt-eyebrow">Company · iii. Leave Budget</span>
        </div>
        <h2 className="leave-hero-title">
          Annual leave <em className="gt-serif-italic">allocations</em>
        </h2>
        <p className="leave-hero-sub">
          Define how many days each employee receives per year and when the leave year resets.
          Saving here refreshes every employee's balance ledger immediately.
        </p>
        <button
          type="button"
          onClick={() => setPolicyOpen(true)}
          className="gt-btn is-primary"
          style={{ position: "absolute", top: 0, right: 0 }}
        >
          <ClipboardList className="w-4 h-4" />
          Leave Policy
        </button>
      </header>

      <div className="leave-grid">
        {/* ── Left column: Configuration ────────────────────────────── */}
        <div className="leave-col-form">
          {/* Section i — Leave year */}
          <section className="leave-section">
            <div className="leave-section-head">
              <span className="leave-section-num">i.</span>
              <div>
                <h3 className="leave-section-title">Leave year</h3>
                <p className="leave-section-sub">
                  The 12-month window over which allocations apply.
                </p>
              </div>
            </div>
            <div className="leave-year-row">
              <label className="leave-field-label" htmlFor="year-start">
                Starts in
              </label>
              <select
                id="year-start"
                className="leave-select"
                value={form.year_start_month}
                onChange={(e) => setField("year_start_month", Number(e.target.value))}
              >
                {MONTHS.map((m, idx) => (
                  <option key={m} value={idx + 1}>
                    {m}
                  </option>
                ))}
              </select>
              <span className="leave-year-hint">
                Window: <strong>{leaveYearLabel(form.year_start_month)}</strong>
              </span>
            </div>
          </section>

          <hr className="leave-rule" />

          {/* Section ii — Default types */}
          <section className="leave-section">
            <div className="leave-section-head">
              <span className="leave-section-num">ii.</span>
              <div>
                <h3 className="leave-section-title">Default leave types</h3>
                <p className="leave-section-sub">
                  Every employee receives these allocations each leave year.
                </p>
              </div>
            </div>

            <div className="leave-types">
              {[
                {
                  key: "sick_days" as const,
                  label: "Sick",
                  hint: "Illness, recovery, medical appointments.",
                },
                {
                  key: "annual_days" as const,
                  label: "Annual",
                  hint: "Planned vacation and holidays.",
                },
                {
                  key: "casual_days" as const,
                  label: "Casual",
                  hint: "Personal time, short-notice absences.",
                },
              ].map((row) => (
                <div key={row.key} className="leave-type-row">
                  <div className="leave-type-meta">
                    <div className="leave-type-name">{row.label}</div>
                    <div className="leave-type-hint">{row.hint}</div>
                  </div>
                  <Stepper
                    value={form[row.key] as number}
                    onChange={(n) => setField(row.key, n as never)}
                    unit="days/yr"
                  />
                </div>
              ))}
            </div>
          </section>

          <hr className="leave-rule" />

          {/* Section iii — Add-ons */}
          <section className="leave-section">
            <div className="leave-section-head">
              <span className="leave-section-num">iii.</span>
              <div>
                <h3 className="leave-section-title">Add-ons</h3>
                <p className="leave-section-sub">
                  Optional per-occurrence leave types. Toggle on to make them available
                  to eligible employees.
                </p>
              </div>
            </div>

            <div className="leave-types">
              {[
                {
                  enabledKey: "maternity_enabled" as const,
                  daysKey: "maternity_days" as const,
                  label: "Maternity",
                  hint: "Female employees file a request specifying the days they need; HR / their reporting person approves it to grant the balance.",
                  showStepper: false,
                },
                {
                  enabledKey: "paternity_enabled" as const,
                  daysKey: "paternity_days" as const,
                  label: "Paternity",
                  hint: "Male employees file a request specifying the days they need; HR / their reporting person approves it to grant the balance.",
                  showStepper: false,
                },
                {
                  enabledKey: "compensatory_enabled" as const,
                  daysKey: "compensatory_days" as const,
                  label: "Compensatory",
                  hint: "Employees file a request specifying the days they need (e.g. for working on a holiday); HR / their reporting person approves it to grant the balance.",
                  showStepper: false,
                },
              ].map((row) => {
                const isOn = form[row.enabledKey] as boolean;
                const showStepper = row.showStepper !== false;
                return (
                  <div
                    key={row.enabledKey}
                    className={`leave-type-row leave-type-row-toggle ${isOn ? "is-on" : ""}`}
                  >
                    <div className="leave-type-meta">
                      <div className="leave-type-name-row">
                        <PillToggle
                          checked={isOn}
                          onChange={(v) => setField(row.enabledKey, v as never)}
                          label={row.label}
                        />
                        <span className="leave-type-name">{row.label}</span>
                      </div>
                      <div className="leave-type-hint">{row.hint}</div>
                    </div>
                    {showStepper && (
                      <div className={`leave-stepper-wrap ${isOn ? "is-revealed" : ""}`}>
                        <Stepper
                          value={form[row.daysKey] as number}
                          onChange={(n) => setField(row.daysKey, n as never)}
                          disabled={!isOn}
                          unit={(row as { unit?: string }).unit || "days/use"}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        {/* ── Right column: Live preview ────────────────────────────── */}
        <aside className="leave-col-aside">
          <div className="leave-preview">
            <div className="gt-eyebrow-row">
              <span className="gt-leaf" />
              <span className="gt-eyebrow">Preview</span>
            </div>
            <h3 className="leave-preview-title">
              Per <em className="gt-serif-italic">employee</em>
            </h3>

            <div className="leave-preview-window">
              <div className="leave-preview-window-label">Leave year</div>
              <div className="leave-preview-window-range">
                {leaveYearLabel(form.year_start_month)}
              </div>
            </div>

            <div className="leave-preview-total">
              <span className="leave-preview-total-num">{totalAnnualDays}</span>
              <span className="leave-preview-total-unit">
                annual days <br />
                <span className="leave-preview-total-fine">
                  before maternity / paternity
                </span>
              </span>
            </div>

            <ul className="leave-preview-list">
              <li>
                <span className="leave-preview-key">Sick</span>
                <span className="leave-preview-val">{form.sick_days}d</span>
              </li>
              <li>
                <span className="leave-preview-key">Annual</span>
                <span className="leave-preview-val">{form.annual_days}d</span>
              </li>
              <li>
                <span className="leave-preview-key">Casual</span>
                <span className="leave-preview-val">{form.casual_days}d</span>
              </li>
              {form.maternity_enabled && (
                <li className="leave-preview-extra">
                  <span className="leave-preview-key">Maternity</span>
                  <span className="leave-preview-val">
                    <span className="leave-preview-once">on request</span>
                  </span>
                </li>
              )}
              {form.paternity_enabled && (
                <li className="leave-preview-extra">
                  <span className="leave-preview-key">Paternity</span>
                  <span className="leave-preview-val">
                    <span className="leave-preview-once">on request</span>
                  </span>
                </li>
              )}
              {form.compensatory_enabled && (
                <li className="leave-preview-extra">
                  <span className="leave-preview-key">Compensatory</span>
                  <span className="leave-preview-val">
                    <span className="leave-preview-once">on request</span>
                  </span>
                </li>
              )}
            </ul>

            <p className="leave-preview-foot">
              Unused days roll over <em className="gt-serif-italic">in full</em> at year-end.
              Withdrawals are blocked once the leave start date passes.
            </p>
          </div>
        </aside>
      </div>

      {/* Save bar */}
      <div className="leave-savebar">
        <div className="leave-savebar-meta">
          {totalAnnualDays} day{totalAnnualDays === 1 ? "" : "s"} per employee · year starts{" "}
          {MONTH_SHORT[form.year_start_month - 1]}
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="leave-save-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? "Saving" : "Save settings"}
        </button>
      </div>

      {/* Leave Policy dialog — deductions, working days, overtime rules.
          Same persistence backend as before; just lives behind a button
          instead of a sibling tab. */}
      <Dialog open={policyOpen} onOpenChange={setPolicyOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Leave Policy</DialogTitle>
            <DialogDescription>
              Deductions, working days, and overtime rules applied by payroll.
            </DialogDescription>
          </DialogHeader>
          <LeavePolicySettings />
        </DialogContent>
      </Dialog>
    </div>
  );
}
